import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import get from 'lodash-es/get'
import isUndefined from 'lodash-es/isUndefined'
import { useImmer } from 'use-immer'

import { GRAPHQL_SERVER_HOST } from '~/config'
import { RESPONSE_MESSAGE } from '~/config/constants'
import { useGetPublicTrackingLazyQuery } from '~/gqlcodegen/fms/hooks/vehicles'
import useSearchQuery from '~/utils/hooks/useQueryParams'
import useSnackbar from '~/utils/hooks/useSnackbar'

import {
  constructDetailData,
  defaultDetailData,
  DetailVehicleData,
  updateVehiclePositionData,
} from '../Detail/helper'
import { appendTrip, createTrips, TripPoint } from '../location'

const useCustom = () => {
  const searchQuery = useSearchQuery()
  const { openSnackbar } = useSnackbar()

  const lastData = useRef<any>({
    status: '',
  })
  const expiryInitialized = useRef(false)

  const [trips, setTrips] = useState<TripPoint[]>([])

  const [detailData, setDetailData] =
    useImmer<DetailVehicleData>(defaultDetailData)

  const [loadPublicDetailData, { loading }] = useGetPublicTrackingLazyQuery({
    fetchPolicy: 'network-only',
    context: {
      clientName: GRAPHQL_SERVER_HOST.FMS,
    },
  })

  const [isAnyData, setIsAnyData] = useState(null)
  const [remainingSecs, setRemainingSecs] = useState<number | null>(null)
  const [loadPublicDetailDataUpdate, { data: publishedData }] =
    useGetPublicTrackingLazyQuery({
      fetchPolicy: 'network-only',
      context: {
        clientName: GRAPHQL_SERVER_HOST.FMS,
      },
    })

  const handleLoadPublicDetailData = useCallback(() => {
    if (!searchQuery.id) return
    loadPublicDetailData({
      variables: {
        id: searchQuery.id as string,
      },
      onCompleted: (data) => {
        const res = data?.vehicles?.GetPublicTracking
        setIsAnyData(res?.data)
        const construcData = constructDetailData(res?.data as any)
        setDetailData(construcData as any)
        setTrips(createTrips(construcData?.lat, construcData?.lng))

        // Parse livetrack_expiry_secs from position properties
        const properties: { field?: string; value?: string }[] =
          res?.data?.position?.properties || []
        const expiryProp = properties.find(
          (p) => p.field === 'livetrack_expiry_secs'
        )
        if (expiryProp?.value && !expiryInitialized.current) {
          expiryInitialized.current = true
          const secs = Math.floor(parseFloat(expiryProp.value))
          setRemainingSecs(secs)
        }
      },
    })
  }, [loadPublicDetailData, searchQuery.id])

  useEffect(() => {
    lastData.current = detailData

    setTrips(createTrips(detailData?.lat, detailData?.lng))
  }, [detailData])

  const handleLoadPublicDetailDataUpdate = useCallback(() => {
    if (!searchQuery.id) return
    loadPublicDetailDataUpdate({
      pollInterval: 10000,
      variables: {
        id: searchQuery.id as string,
      },
    })
  }, [loadPublicDetailDataUpdate, searchQuery.id])

  const handleClickGoogleMapsShare = useCallback((lat: number, lng: number) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    navigator.clipboard
      .writeText(url)
      .then(() => {
        openSnackbar(
          'Google Maps URL copied to clipboard',
          true,
          RESPONSE_MESSAGE.DEFAULT,
          'copy'
        )
      })
      .catch(() => {
        openSnackbar(
          'Failed to copy Google Maps URL',
          false,
          RESPONSE_MESSAGE.DEFAULT,
          'copy-fail'
        )
      })
    // window.open(url, '_blank')
  }, [])

  useEffect(() => {
    if (
      !isUndefined(lastData.current?.vehicleID) &&
      lastData.current?.vehicleID === get(publishedData, 'data.vehicle.ID', '')
    ) {
      lastData.current = updateVehiclePositionData(
        publishedData?.vehicles?.GetPublicTracking?.data as any,
        lastData.current?.deviceModel
      )
      const latestStatus: string = get(
        publishedData?.vehicles?.GetPublicTracking?.data,
        'data.position.status',
        'stop'
      )
      const nextTrips = createTrips(
        lastData.current?.lat,
        lastData.current?.lng
      )

      if (latestStatus.toLowerCase() === 'stop') {
        setTrips((prevTrips) => (nextTrips.length > 0 ? nextTrips : prevTrips))
      } else {
        setTrips((prevTrips) =>
          appendTrip(prevTrips, lastData.current?.lat, lastData.current?.lng)
        )
      }
    }
  }, [publishedData])

  useEffect(() => {
    handleLoadPublicDetailData()
  }, [handleLoadPublicDetailData])

  useEffect(() => {
    handleLoadPublicDetailDataUpdate()
  }, [handleLoadPublicDetailDataUpdate])

  // Countdown timer
  useEffect(() => {
    if (remainingSecs === null) return
    if (remainingSecs <= 0) return
    const interval = setInterval(() => {
      setRemainingSecs((prev) => (prev !== null && prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [remainingSecs === null])

  const expiryLabel = useMemo(() => {
    if (remainingSecs === null) return null
    if (remainingSecs <= 0) return 'Expired'
    const h = Math.floor(remainingSecs / 3600)
    const m = Math.floor((remainingSecs % 3600) / 60)
    const s = remainingSecs % 60
    const parts: string[] = []
    if (h > 0) parts.push(`${h}h`)
    if (m > 0) parts.push(`${m}m`)
    parts.push(`${s}s`)
    return `Expires in ${parts.join(' ')}`
  }, [remainingSecs])

  return {
    data: {
      detailData,
      expiryLabel,
      isAnyData,
      lastData,
      loading,
      remainingSecs,
      searchQuery,
      trips,
    },
    methods: {
      handleClickGoogleMapsShare,
    },
  }
}

export default useCustom
